import { FocusMonitor } from '@angular/cdk/a11y';
import { NgTemplateOutlet } from '@angular/common';
import {
    AfterContentInit,
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ContentChild,
    ContentChildren,
    ElementRef,
    OnDestroy,
    QueryList,
    TemplateRef,
    ViewChild,
    ViewChildren,
    ViewEncapsulation,
    booleanAttribute,
    computed,
    effect,
    inject,
    input,
    model,
    output,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { uniqueId } from 'lodash-es';

import { DsTab } from './tab.component';
import { DsTabsScrollArrow } from './tabs-arrow.component';
import { DsTabsArrows } from './tabs-arrows';
import { TabsGroupIndicatorType, TabsGroupSizesType, TabsGroupVariantsType } from './tabsgroup.types';

@Component({
    selector: 'ds-tabs-group',
    templateUrl: './tabsgroup.component.html',
    host: {
        '[class]': `hostClass()`,
        '[class.ds-tabs-inverse]': 'inverse()',
    },
    imports: [NgTemplateOutlet, DsTabsScrollArrow],
    styleUrl: './tabsgroup.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DsTabsGroup extends DsTabsArrows implements AfterViewInit, AfterContentInit, OnDestroy {
    private readonly _focusMonitor = inject(FocusMonitor);

    /**
     * The active tab.
     * This is the tab that is currently selected.
     * We use a signal to make sure that the active tab is updated when the tab is selected.
     *
     * The name of the active tab.
     * If not provided, the first tab will be selected.
     *
     * @internal
     */

    public readonly activeTab = model('');

    /**
     * Event emitted when a tab selection changed.
     * The event payload is an object with the name of the tab and the event that triggered the click.
     */
    public readonly tabChanged = output<{ name: string; event: MouseEvent }>();

    /** The size of the tabs. (small or large) */
    public readonly size = model<TabsGroupSizesType>('large');

    /** The variant of the tabs. (vertical or horizontal) */
    public readonly variant = model<TabsGroupVariantsType>('vertical');

    /** The indicator of the tabs. (underline or fill) */
    public readonly indicator = model<TabsGroupIndicatorType>('underline');

    /**
     * Use inverse style for tabs when set to true.
     */
    public readonly inverse = model(false);

    /**
     * The aria label for the previous navigation arrow.
     * This is used by screen readers to announce the navigation arrow.
     */
    public readonly ariaLabelNavigationArrowPrev = input('prev');

    /**
     * The aria label for the next navigation arrow.
     * This is used by screen readers to announce the navigation arrow.
     */
    public readonly ariaLabelNavigationArrowNext = input('next');

    /**
     * The id of the tabs group.
     * This is used to generate unique ids for the tabs.
     *
     * @internal
     */
    protected readonly id = uniqueId('ds-tab-id-');
    /*
     * Whether the tabs should take up the full width of the container.
     *
     * The default value is based on the tab options which can be configured using the `provideTabOptions` function.
     */
    public readonly fullWidthTabs = input(this.tabOptions.fullWidthTabs, { transform: booleanAttribute });

    /**
     * The tabs.
     * This is a list of tabs that are used to render the tabs.
     * Each tab is a `DsTab` component.
     * Example:
     * ```html
     * <ds-tabs-group>
     *   <ds-tab [name]="'tab1'" [title]="'Tab 1'">Tab 1</ds-tab>
     *   <ds-tab [name]="'tab2'" [title]="'Tab 2'">Tab 2</ds-tab>
     * </ds-tabs-group>
     * ```
     */
    @ContentChildren(DsTab) protected readonly tabs: QueryList<DsTab> | undefined;

    protected _tabs = signal<DsTab[]>([]);

    /**
     * `TemplateRef` for the left arrow.
     *
     * @example
     * ```html
     * <ds-tabs-group>
     *   ...
     *   <ng-template #dsTabsLeftArrow> <- </ng-template>
     * </ds-tabs-group>
     * ```
     */
    @ContentChild('dsTabsLeftArrow') protected readonly dsTabsLeftArrowTpl?: TemplateRef<any>;

    /**
     * `TemplateRef` for the right arrow.
     *
     * @example
     * ```html
     * <ds-tabs-group>
     *   ...
     *   <ng-template #dsTabsRightArrow> -> </ng-template>
     * </ds-tabs-group>
     * ```
     */
    @ContentChild('dsTabsRightArrow') protected readonly dsTabsRightArrowTpl?: TemplateRef<any>;

    /**
     * The tab header items container.
     * This is the container that holds the tab header list.
     *
     * @internal
     */
    @ViewChild('tabHeaderItemsContainer', { static: true })
    protected readonly tabHeaderItemsContainer!: ElementRef<HTMLElement>;

    /**
     * The tab header items list.
     * This is the list that holds the tab header items.
     *
     * @internal
     */
    @ViewChild('tabHeaderItemsList', { static: true })
    public readonly tabHeaderItemsList!: ElementRef<HTMLUListElement>;

    @ViewChildren('tabItem') public readonly tabItems?: QueryList<ElementRef>;

    constructor() {
        effect(() => {
            const activeTab = this.activeTab().toString(); // depends on active tab signal
            const tabsArray = this._tabs(); // depends on tabs signal

            tabsArray.forEach((tab) => {
                tab.selected.set(tab.name() === activeTab);
            });
        });

        super();
    }

    ngAfterViewInit(): void {
        if (this.tabItems) {
            this.tabItems.forEach((option, index) => {
                this._focusMonitor.monitor(option.nativeElement, true).subscribe((focusOrigin) => {
                    this._tabs()[index]?.setFocused(focusOrigin === 'keyboard' || focusOrigin === 'program');
                });
            });
        }
    }

    override ngAfterContentInit() {
        this.selectTab(this.activeTab().toString());

        if (this.tabs) {
            this.tabs.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
                const tabs = this.tabs?.toArray() ?? [];
                this._tabs.set(tabs);

                // if there are no tabs, do nothing
                if (tabs.length === 0) {
                    return;
                }

                // check if the selected tab still exists
                const selectedTab = this.selectedTab();
                if (!selectedTab) {
                    // try to select the previously selected tab name if it exists
                    if (this.activeTab()) {
                        this.selectTab(this.activeTab());
                    } else {
                        // select the first tab if the selected tab does not exist
                        this.selectTab(tabs[0]?.name() ?? '');
                    }
                }
            });

            this._tabs.set(this.tabs.toArray() ?? []);

            let initialSelection = this.activeTab() || this.tabs.first.name();
            this.selectTab(initialSelection);

            this.tabs?.forEach((tab) => {
                tab.selected.set(tab.name() === initialSelection);
            });

            super.ngAfterContentInit();
        }
    }

    /**
     * Selects a tab by name.
     * This will set the `selected` property of the tab to `true` and emit the `activeTabChange` event.
     *
     * @param name - The name of the tab to select.
     * @param event - The event that triggered the selection.
     */
    selectTab(name: string, event?: MouseEvent) {
        this.activeTab.set(name);

        if (event) {
            this.tabChanged.emit({ name, event });
        }
    }

    /**
     * Returns the selected tab.
     * This will return the first tab that is selected.
     *
     * @returns The selected tab.
     * @internal
     */
    private selectedTab(): DsTab | undefined {
        return this.tabs?.find((tab) => tab.selected());
    }

    /**
     * Returns the host class based on the size and indicator.
     *
     * @returns The host class.
     * @internal
     */
    protected readonly hostClass = computed(() => {
        const sizeClass = this.size() === 'small' ? 'ds-tabs-small' : 'ds-tabs-large';
        const indicatorClass = this.indicator() === 'underline' ? 'ds-tab-underline' : 'ds-tab-fill';

        return `ds-tabs-group ${sizeClass} ${indicatorClass}`;
    });

    protected readonly tabHeaderItemsClass = computed(() => {
        return `ds-tab-header-items ds-tab-items-scroll-speed-${this.scrollSpeed()}`;
    });

    focusOption(tab: DsTab, index: number) {
        const focusOption = this.tabItems?.get(index);
        if (focusOption) {
            this._tabs().forEach((tabOptionObj) => {
                tabOptionObj.setFocused(false);
            });
            tab.setFocused(true);
            this._focusMonitor.focusVia(focusOption.nativeElement, 'keyboard');
        }
    }

    onKeydown(event: KeyboardEvent) {
        const { key } = event;
        const tabsList = this._tabs();
        const currentIndex = tabsList.findIndex((tab) => tab.focused());
        let newIndex: number | undefined;
        const nextFocusTabIndex = this.getNextFocusTabIndex(key, currentIndex);
        if (key === 'ArrowRight') {
            newIndex = (currentIndex + nextFocusTabIndex) % tabsList.length;
        } else if (key === 'ArrowLeft') {
            newIndex = (currentIndex - nextFocusTabIndex + tabsList.length) % tabsList.length;
        } else if ((key === ' ' || key === 'Enter') && currentIndex !== -1) {
            const currentTab = tabsList[currentIndex];
            if (currentTab) {
                this.selectTab(currentTab.name());
            }
        }

        if (newIndex !== undefined) {
            event.preventDefault();
            const newTab = tabsList[newIndex];
            if (newTab) {
                this.focusOption(newTab, newIndex);
            }
        }
    }

    getNextFocusTabIndex(key: string, currentIndex: number): number {
        const tabs = this._tabs(); // Get the list of tabs
        if (key === 'ArrowRight' && currentIndex + 1 < tabs.length) {
            const nextTab = tabs[currentIndex + 1];
            if (nextTab) {
                return nextTab.disabled() ? 2 : 1;
            }
        } else if (key === 'ArrowLeft' && currentIndex - 1 >= 0) {
            const prevTab = tabs[currentIndex - 1];
            if (prevTab) {
                return prevTab.disabled() ? 2 : 1;
            }
        }
        return 1;
    }

    ngOnDestroy() {
        if (this.tabItems) {
            this.tabItems.forEach((option) => this._focusMonitor.stopMonitoring(option));
        }
    }
}
